"use client";

import { Chart, useChart } from "@chakra-ui/charts";
import { Box } from "@chakra-ui/react";
import {
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const DEFAULT_ASSESSMENT_SCORE = {
  d1: 0,
  d2: 0,
  d3: 0,
  d4: 0,
} as const;

const DEFAULT_RADAR_CHART_COLOR = "blue.solid";
const DEFAULT_RADAR_CHART_HEIGHT = "64";

export type RadarChartColor = "blue.solid" | "purple.solid" | "green.solid";
export type GenericRadarChartScore = {
  d1: number;
  d2: number;
  d3: number;
  d4: number;
};

export type GenericRadarChartProps = {
  assessmentScore?: GenericRadarChartScore;
  chartColor?: RadarChartColor;
  height?: string;
};

function getRadarChartData(assessmentScore: GenericRadarChartScore) {
  return [
    { score: assessmentScore.d1, dimension: "D1" },
    { score: assessmentScore.d2, dimension: "D2" },
    { score: assessmentScore.d3, dimension: "D3" },
    { score: assessmentScore.d4, dimension: "D4" },
  ];
}

export default function GenericRadarChart({
  assessmentScore = DEFAULT_ASSESSMENT_SCORE,
  chartColor = DEFAULT_RADAR_CHART_COLOR,
  height = DEFAULT_RADAR_CHART_HEIGHT,
}: GenericRadarChartProps) {
  const chartData = getRadarChartData(assessmentScore);

  const chart = useChart({
    data: chartData,
    series: [{ name: "score", color: chartColor }],
  });

  return (
    <Box maxW="3xs" minH={height} minW="0" position="relative" w="full">
      <Chart.Root
        aspectRatio="auto"
        chart={chart}
        h={height}
        minH={height}
        minW="0"
        w="full"
      >
        <ResponsiveContainer
          height="100%"
          initialDimension={{ width: 224, height: 256 }}
          minHeight={0}
          minWidth={0}
          width="100%"
        >
          <RadarChart data={chart.data}>
            <PolarGrid stroke={chart.color("border")} gridType="circle" />
            <PolarAngleAxis dataKey={chart.key("dimension")} />
            <Tooltip content={<Chart.Tooltip />} />
            {chart.series.map((item) => (
              <Radar
                dot={{ fillOpacity: 1 }}
                isAnimationActive={false}
                key={item.name}
                name={item.name}
                dataKey={chart.key(item.name)}
                stroke={chart.color(item.color)}
                fill={chart.color(item.color)}
                fillOpacity={0.2}
              />
            ))}
          </RadarChart>
        </ResponsiveContainer>
      </Chart.Root>
    </Box>
  );
}
